import React, { useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useInventory } from '../ViewInventory/hooks/useInventory'
import { useDragDrop } from '../ViewInventory/hooks/useDragDrop'

import '../ViewInventory/ViewCharacterInventory.css'
import './CharacterDetail.css'
import EquipmentMannequin from '../ViewInventory/components/EquipmentMannequin'
import CharacterInventory from '../ViewInventory/components/CharacterInventory'
import useCharacterWounds from '../shared/useCharacterWounds'

export default function ViewCharacterSheet() {
  const { slug } = useParams()
  const {
    character,
    equipment,
    inventory,
    loading,
    error,
    handleEquipItem,
    handleUnequipItem,
    handleMoveItem,
    handleSwapEquipment,
    canEquipItemInSlot,
    handleClearError,
    getTotalDamage,
  } = useInventory()

  const { handleDragStart, handleDragOver, handleDragEnter, handleDragLeave, handleDrop, handleDragEnd, getDragClasses } =
    useDragDrop()

  const { woundZones } = useCharacterWounds(slug)

  useEffect(() => {
    if (error) {
      console.error('Error en equipo:', error)
      const timer = setTimeout(() => {
        handleClearError()
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [error, handleClearError])

  const handleDropWithCallbacks = (e, targetType, targetIndex) => {
    handleDrop(
      e,
      targetType,
      targetIndex,
      handleEquipItem,
      handleUnequipItem,
      handleMoveItem,
      canEquipItemInSlot,
      handleSwapEquipment
    )
  }

  const damage = getTotalDamage()
  const itemCount = inventory.items?.filter(Boolean).length || 0
  const maxSlots = inventory.maxSlots || 30

  if (loading && !character.name) {
    return (
      <div className="char-equipo">
        <div className="char-equipo__loading">
          <div className="char-ficha__loading-spinner" aria-hidden="true" />
          <p>Cargando equipo…</p>
        </div>
      </div>
    )
  }

  if (character?.academyMode) {
    return (
      <div className="char-equipo">
        <div className="char-locked-panel" role="status">
          <span className="char-locked-panel__glyph" aria-hidden="true">
            具
          </span>
          <h2 className="char-locked-panel__title">Equipo bloqueado</h2>
          <p className="char-locked-panel__body">
            El inventario y el equipo se desbloquean al graduarte de la academia.
          </p>
          <Link to={`/personajes/${slug}/ficha`} className="char-btn-accent">
            Volver a la ficha
          </Link>
        </div>
      </div>
    )
  }

  if (!loading && error && !character.name) {
    return (
      <div className="char-equipo">
        <header className="char-equipo__masthead">
          <div className="char-equipo__masthead-accent" aria-hidden="true" />
          <div className="char-equipo__masthead-body">
            <div className="char-equipo__masthead-row">
              <div className="char-equipo__masthead-intro">
                <span className="char-equipo__masthead-mark" aria-hidden="true">具</span>
                <div className="char-equipo__masthead-text">
                  <p className="char-equipo__masthead-eyebrow">Arsenal del shinobi</p>
                  <h2 className="char-equipo__title">Equipo e inventario</h2>
                </div>
              </div>
            </div>
          </div>
        </header>
        <p className="char-equipo__error" role="alert">
          No se pudo cargar el inventario: {error}
        </p>
      </div>
    )
  }

  return (
    <div className="char-equipo">
      <header className="char-equipo__masthead">
        <div className="char-equipo__masthead-accent" aria-hidden="true" />
        <div className="char-equipo__masthead-body">
          <div className="char-equipo__masthead-row">
            <div className="char-equipo__masthead-intro">
              <span className="char-equipo__masthead-mark" aria-hidden="true">
                具
              </span>
              <div className="char-equipo__masthead-text">
                <p className="char-equipo__masthead-eyebrow">Arsenal del shinobi</p>
                <h2 className="char-equipo__title">Equipo e inventario</h2>
              </div>
            </div>

            <div className="char-equipo__masthead-actions">
              <div className="char-equipo__summary-chips">
                <span className="char-equipo__chip">
                  Daño {damage.min}–{damage.max}
                </span>
                <span className="char-equipo__chip">
                  {itemCount}/{maxSlots} objetos
                </span>
              </div>
              {slug ? (
                <Link to={`/personajes/${slug}`} className="char-btn char-btn--ghost char-equipo__ficha-link">
                  Ver ficha
                </Link>
              ) : null}
            </div>
          </div>

          <div className="char-equipo__masthead-rule" aria-hidden="true">
            <span className="char-equipo__masthead-rule-line" />
            <span className="char-equipo__masthead-rule-gem">◆</span>
            <span className="char-equipo__masthead-rule-line" />
          </div>
        </div>
      </header>

      {error ? (
        <p className="char-equipo__error" role="alert">
          Error: {error}
        </p>
      ) : null}

      <div className="char-equipo__body">
        <EquipmentMannequin
          character={character}
          slug={slug}
          equipment={equipment}
          woundZones={woundZones}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDropWithCallbacks}
          getDragClasses={getDragClasses}
          canEquipItemInSlot={canEquipItemInSlot}
          onUnequipItem={handleUnequipItem}
        />

        <CharacterInventory
          variant="embedded"
          inventory={inventory}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDropWithCallbacks}
          getDragClasses={getDragClasses}
          onMoveItem={handleMoveItem}
        />
      </div>
    </div>
  )
}
